import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
  Link
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { API_BASE_URL } from '@config';

const useStyles = makeStyles((theme) => ({
  codeContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(2)
  },
  codeInput: {
    width: 48,
    height: 56,
    fontSize: 24,
    textAlign: 'center',
    fontWeight: 'bold'
  },
  button: {
    marginTop: theme.spacing(1)
  },
  resendContainer: {
    textAlign: 'center',
    marginTop: theme.spacing(3)
  },
  resendLink: {
    cursor: 'pointer',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(2)
  },
  successText: {
    color: theme.palette.success.main,
    marginTop: theme.spacing(2)
  }
}));

// Matches RESEND_INTERVAL_MS in the api's service/campaignAccessCodeService.js.
const RESEND_INTERVAL_SECONDS = 60;

const CampaignVerificationCode = ({ email, campaignSlug, onSuccess, onError }) => {
  const classes = useStyles();
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(RESEND_INTERVAL_SECONDS);
  const inputRefs = useRef([]);

  // One ticking countdown. It starts at RESEND_INTERVAL_SECONDS because a code
  // was sent just before this screen opened, and the server refuses a new one
  // until the interval has passed.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((prev) => Math.max(prev - 1, 0)), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only keep last character
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Backspace: move to previous input
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }
    // Arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }
    if (e.key === 'ArrowRight' && index < 5) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const digits = pastedData.replace(/\D/g, '').slice(0, 6);

    if (digits.length > 0) {
      const newCode = digits.split('');
      // Pad with empty strings if less than 6 digits
      while (newCode.length < 6) {
        newCode.push('');
      }
      setCode(newCode);
      // Focus the next empty field or the last field
      const nextEmptyIndex = newCode.findIndex((d) => !d);
      const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');

    if (fullCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/campaign/verify-and-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          code: fullCode,
          campaignSlug: campaignSlug
        })
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.message || 'Verification failed';
        setError(errorMsg);
        if (onError) {
          onError(errorMsg);
        }
        setLoading(false);
        return;
      }

      // Success
      if (onSuccess && data.token) {
        onSuccess(data.token);
      }
    } catch (err) {
      console.error('Verify error:', err);
      const errorMsg = 'Verification failed. Please try again.';
      setError(errorMsg);
      if (onError) {
        onError(errorMsg);
      }
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/campaign/resend-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          campaignSlug: campaignSlug
        })
      });

      if (response.ok) {
        setResendCooldown(RESEND_INTERVAL_SECONDS);
        setError('');
      } else if (response.status === 429) {
        // Asked too soon: wait out what the server says is left.
        const data = await response.json().catch(() => ({}));
        const retryAfter = parseInt(data.retryAfterSeconds || response.headers.get('Retry-After'), 10);
        setResendCooldown(retryAfter > 0 ? retryAfter : RESEND_INTERVAL_SECONDS);
        setError('');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to resend code');
      }
    } catch (err) {
      console.error('Resend error:', err);
      setError('Failed to resend code');
    }
  };

  const handleEnterKey = (e) => {
    if (e.key === 'Enter' && code.join('').length === 6 && !loading) {
      handleVerify();
    }
  };

  return (
    <Box>
      <Box className={classes.codeContainer} onPaste={handlePaste}>
        {code.map((digit, index) => (
          <TextField
            key={index}
            ref={(ref) => (inputRefs.current[index] = ref)}
            value={digit}
            onChange={(e) => handleCodeChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onKeyPress={handleEnterKey}
            className={classes.codeInput}
            inputProps={{
              maxLength: 1,
              style: { textAlign: 'center', fontSize: 24 },
              autoComplete: 'off'
            }}
            type="text"
            disabled={loading}
            variant="outlined"
          />
        ))}
      </Box>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleVerify}
        disabled={loading || code.join('').length !== 6}
        className={classes.button}
        size="large"
      >
        {loading ? (
          <>
            <CircularProgress size={20} style={{ marginRight: 8 }} />
            Verifying...
          </>
        ) : (
          'Verify Code'
        )}
      </Button>

      {error && (
        <Typography className={classes.errorText} variant="body2">
          {error}
        </Typography>
      )}

      <Box className={classes.resendContainer}>
        <Typography variant="body2" color="textSecondary">
          Didn't receive a code?{' '}
          <Link
            className={classes.resendLink}
            onClick={handleResend}
            disabled={resendCooldown > 0 || loading}
            style={{
              pointerEvents: resendCooldown > 0 || loading ? 'none' : 'auto',
              opacity: resendCooldown > 0 || loading ? 0.5 : 1,
              color: resendCooldown > 0 || loading ? 'inherit' : 'primary'
            }}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
          </Link>
        </Typography>
      </Box>

      <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 16, textAlign: 'center' }}>
        Code expires in 15 minutes
      </Typography>
    </Box>
  );
};

export default CampaignVerificationCode;
